import styled from 'styled-components';
import { ProductDTO } from 'src/models/pim';
import {
  ProductAttributeCode,
  ProductAttributeCollection,
} from 'src/models/pim/attributes';
import { useTranslateItem } from 'src/hooks/directus/useTranslateItem';
import { ConstraintContainer } from 'src/components/common/ConstraintContainer';
import { ChevronRight } from 'src/components/icons/ChevronRight';
import { Markdown } from 'vendor/mods/markdown/components/Markdown';
import { FC, useMemo } from 'react';
import { Heading } from 'src/components/common/Heading';
import { media } from 'src/styles/designs';
import { head } from 'lodash';
import { useProductAttributes } from 'src/modules/products/hooks/useProductAttributes';
import { useTitlesDictionary } from 'src/modules/products/hooks/useTitlesDictionary';
import { DictionaryTitlesCode } from 'src/models/pim/dictionaries';
import { Paragraph } from 'src/components/common/typography/Text';
import { StickyMenuSection } from 'src/components/common/StickyMenu/StickyMenuSection';

export interface ProductPageBenefitsSectionProps {
  product: ProductDTO;
  descriptionCode?: ProductAttributeCode;
  dictionaryTitleCode?: DictionaryTitlesCode;
  titleCode?: ProductAttributeCode;
  textCode?: ProductAttributeCode;
}

const Section = styled(StickyMenuSection)`
  padding: 32px 0 48px;
  background: ${({ theme }) => theme.palette.background};

  & + & {
    padding-top: 0;
  }

  ${media.w.greaterThanOrEqual.desktop} {
    padding: 58px 0;

    & + & {
      padding-top: 16px;
    }
  }
`;

const ListContainer = styled.ul`
  margin-top: 21px;
`;

const ListInnerContainer = styled.div`
  display: grid;

  ${media.w.greaterThanOrEqual.desktop} {
    grid-template-columns: repeat(3, 1fr);
    gap: 21px;
  }
`;

const ListItem = styled.li`
  display: grid;
  grid-template-columns: 16px 1fr;
  gap: 12px;

  padding: 16px 0;

  border-top: 1rpx solid ${({ theme }) => theme.skeleton.base};
  color: ${({ theme }) => theme.text.palette.accent};

  & > svg {
    margin-top: 4px;
  }
`;

const Label = styled.h3`
  font-weight: 700;
  font-size: 18px;
  text-transform: uppercase;
  margin-bottom: 16px;
`;

const Description = styled(Paragraph)`
  max-width: 540px;
`;

export const ProductPageBenefitsSection: FC<
  ProductPageBenefitsSectionProps
> = ({
  product,
  titleCode,
  textCode,
  dictionaryTitleCode,
  descriptionCode,
}) => {
  const translateFn = useTranslateItem();

  const { getProductAttribute } = useProductAttributes(product.attributes);

  const [dictionaryTitle] = useTitlesDictionary(dictionaryTitleCode);

  const title =
    head(
      getProductAttribute(
        titleCode as unknown as ProductAttributeCollection.TextValues,
      ),
    ) ?? dictionaryTitle;

  const description = head(
    getProductAttribute(
      descriptionCode as unknown as ProductAttributeCollection.TextValues,
    ),
  );

  const list = useMemo(() => {
    const benefitsAttribute = head(
      getProductAttribute(
        textCode as unknown as ProductAttributeCollection.TextValues,
      ),
    );

    if (benefitsAttribute) {
      const titles = translateFn(benefitsAttribute)
        .value.split('###')
        .filter(Boolean);

      return titles.map((text) => ({
        label: text.startsWith('-') ? '' : text.split('\n')?.[0],
        items:
          text.match(/^(?:\d+\.|[*+-]) .*/gm)?.map((v) => v.slice(2)) ?? [],
      }));
    }

    return [];
  }, [textCode, getProductAttribute, translateFn]);

  if (!title || (list.length === 0 && !description)) {
    return null;
  }

  return (
    <Section
      id="benefits"
      label={translateFn(title).value}
      threshold={{
        desktop: 1,
        mobile: 0.8,
      }}
    >
      <ConstraintContainer>
        <Heading>
          <Markdown>{translateFn(title).value}</Markdown>
        </Heading>

        {description && (
          <Description>
            <Markdown>{translateFn(description).value}</Markdown>
          </Description>
        )}

        {list.map(({ label, items }, index) => (
          <ListContainer key={index}>
            {label && <Label>{label}</Label>}

            <ListInnerContainer>
              {items.map((text) => (
                <ListItem key={text}>
                  <ChevronRight size={14} />
                  <Paragraph>
                    <Markdown>{text}</Markdown>
                  </Paragraph>
                </ListItem>
              ))}
            </ListInnerContainer>
          </ListContainer>
        ))}
      </ConstraintContainer>
    </Section>
  );
};
